<template lang="html">
  <div class="data-table-wrapper pa-0">
    <v-progress-circular
      v-if="loading"
      :size="50"
      color="primary"
      indeterminate
    />
    <div v-else-if="!hasRequiredParams">
      Please fill in all required fields.
    </div>
    <div v-else-if="!(data && data.rows && data.rows.length)">
      No data was found
    </div>
    <div style="width: 100%" v-else>
      <div class="text-right">
        <v-pagination
          v-if="paginationTotalPages > 1 && !isLast10"
          :value="paginationPage"
          :length="paginationTotalPages"
          :total-visible="appSmall ? 5 : 9"
          @input="updatePagination({page: $event})"
        />
      </div>
      <v-simple-table
        style="min-width: 100%;"
        class="elevation-1"
        fill-height
      >
        <thead>
          <tr>
            <th style="width: 60px"></th>
            <th
              v-for="field in fields"
              :key="field.key"
            >
              {{ field.name }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(item, $index) in items"
            :key="$index"
          >
            <td class="layout pt-1">
              <v-btn
                icon
                small
                color="black"
                :disabled="loading"
                @click="showRecord = item"
              >
                <v-icon small>fa-search</v-icon>
              </v-btn>
            </td>
            <td
              v-for="(field, $findex) in fields"
              :key="$findex"
            >
              <div v-if="item[field.key] === 'warning'" style="padding-left: 5px;">
                <v-icon style="color: red">fa-exclamation-circle</v-icon>
              </div>
              <span v-else>{{ item[field.key] || '' }}</span>
            </td>
          </tr>
        </tbody>
      </v-simple-table>
    </div>
    <DataDetailsDialog
      v-if="showRecord"
      :recordId="showRecord.uid"
      :machineId="params.machine"
      @close="showRecord = null"
    />
  </div>
</template>

<style lang="scss">
.data-table-wrapper {
  min-height: 200px;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}
</style>

<script>
import axios from 'axios'
import moment from 'moment-timezone'
import get from 'lodash/get'
import omit from 'lodash/omit'
import TestTypes from '@/lib/TestTypes'
import round from 'lodash/round'
import PaginationMixin from '@/mixins/PaginationMixin'
import DataDetailsDialog from './DataDetailsDialog'

export default {
  mixins: [PaginationMixin],
  components: {
    DataDetailsDialog
  },
  props: {
    params: {
      type: Object,
      required: true
    },
    allowSelectMultiple: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      loading: false,
      data: {},
      showRecord: null
    }
  },
  computed: {
    paginationTotalItems () {
      return this.data.total
    },
    isLast10 () {
      return this.params.dateRange && this.params.dateRange.range === 'last10loads'
    },
    hasRequiredParams () {
      if (this.params.machine && ((this.params.dateRange && this.params.dateRange.range) || (this.params.dateRange && this.params.dateRange.start && this.params.dateRange.end))) {
        return true
      }
      return false
    },
    testType () {
      if (!this.params || !this.params.testType) {
        return
      }
      let testType = TestTypes.find(t => t.id === this.params.testType)

      if (testType) {
        testType = {
          ...testType,
          ...(typeof testType.chart === 'function' ? { chart: testType.chart() } : {})
        }
      }
      return testType
    },
    fields () {
      if (!this.testType) {
        return []
      }
      return (this.testType.fields || [])
    },
    headers () {
      let headers = [
        {
          text: '',
          sortable: false,
          align: 'left',
          width: 70
        }
      ]
      return headers.concat(this.fields.map((field) => ({
        text: field.name,
        align: 'left',
        value: field.key,
        sortable: false
      })))
    },
    items () {
      return ((this.data && this.data.rows) || []).map(record => {
        let newRecord = {
          uid: record.uid
        }
        this.fields.forEach(field => {
          switch (field.type) {
            case 'timestamp':
              newRecord[field.key] = moment(record[field.key]).utc().format('MM/DD/YYYY hh:mm:ss a') // Machine TZ may have used to be displayed after this
              break
            case 'boolean':
              newRecord[field.key] = record[field.key] ? 'warning' : ''
              break
            case 'number':
              newRecord[field.key] = round(record[field.key], field.precision || 2)
              break
            case 'currency':
              newRecord[field.key] = round(record[field.key], 2)
              break
          }
        })
        return newRecord
      })
    }
  },
  methods: {
    async paginationUpdated () {
      console.log('pagination', JSON.stringify(this.pagination))
      await this.loadData()
    },
    async loadData () {
      console.log('load', this.params, JSON.stringify(this.pagination))
      this.loading = true

      if (!this.hasRequiredParams) {
        this.loading = false
        return
      }
      let params = {
        current: this.pagination.page || 1,
        ...(this.isLast10 ? {
          rowCount: this.pagination.rowsPerPage
        } : {
          fromDate: this.params.dateRange && this.params.dateRange.start ? moment(this.params.dateRange.start).format('YYYY-MM-DD[T]HH:mm:ss') : null,
          toDate: this.params.dateRange && this.params.dateRange.end ? moment(this.params.dateRange.end).format('YYYY-MM-DD[T]HH:mm:ss') : null
        })
      }
      if ((!get(this.params, 'dateRange.start') && !get(this.params, 'dateRange.end')) || this.isLast10) {
        params.sort = {
          sortField: 'date',
          sortDir: 'DESC'
        }
      }

      const aYearAgo = moment().subtract(1, 'year')
      if (
        (params.fromDate && moment(params.fromDate).isBefore(aYearAgo)) ||
        (params.toDate && moment(params.toDate).isBefore(aYearAgo))
      ) {
        params = {
          ...params,
          archive: 1
        }
      }

      try {
        let response = await axios({
          method: 'GET',
          url: '/rest/machine/' + this.params.machine + '/data',
          params
        })
        this.data = response.data
        if (this.data.current > 1 && this.data.rowCount * (this.data.current - 1) > this.data.total) {
          return this.resetPagination()
        }
        this.loading = false
      } catch (e) {
        this.loading = false
        throw e
      }
    }
  },
  watch: {
    params (before, after) {
      // No need to reload when only testType changes
      if (JSON.stringify(omit(before, ['testType'])) === JSON.stringify(omit(after, ['testType']))) {
        return
      }
      this.loadData()
    }
  },
  created () {
    this.loadData()
  }
}
</script>
